import { css } from 'aphrodite';
import { Button } from '../../../../../web/components-v2/Button';
import { BackArrowIcon } from '../../../../../web/components/svgs/icons/BackArrowIcon';
import { Select, SelectContent, SelectItem, SelectTrigger } from '../../../../../web/components-v2/Select';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '../../../../../web/components/HoverCard';
import { InfoIcon } from '../../../../../web/components/svgs/icons/InfoIcon';
import { SettingDependencyChunk } from './SettingDependencyChunk';
import { getEntryKey, ISettingEntry } from './settingEntry';
import { SettingCategory } from './Setting';
import { groupByDependency } from './settingsDependency';
import { ISettingsGroup } from './settingsGroups';
import { settingsHubStyles as styles } from './settingsHubStyles';
import { countMatchesByCategory, filterSettings } from './settingsSearch';

interface IProps {
	entries: ISettingEntry[];
	group: ISettingsGroup;
	/** Every group the user may see, for lateral movement without returning to the hub. */
	groups: ISettingsGroup[];
	onOpenGroup: (category: SettingCategory) => void;
	onBackToOverview: () => void;
}

/**
 * One group at a time. Rows are the same `Setting` elements the tab has always rendered, so every
 * control keeps its existing behaviour — only their presentation and what surrounds them changed.
 */
export function SettingsGroupDetail({ entries, group, groups, onOpenGroup, onBackToOverview }: IProps) {
	const groupEntries = filterSettings(entries, { category: group.category });
	const dependencyGroups = groupByDependency(groupEntries);
	const { Icon } = group;
	// One pass for every group's count, so the closed trigger and the list it opens cannot disagree.
	// A group with no rows is absent from the map rather than zero.
	const counts = countMatchesByCategory(entries);
	const optionLabel = (candidate: ISettingsGroup) => `${candidate.category} · ${counts.get(candidate.category) ?? 0}`;

	return (
		<>
			<div className={css(styles.groupToolbar)}>
				{/* One control instead of a trail: the group's name is already the panel's title below and
				    the switcher beside it, so a crumb repeating it earned nothing. */}
				<Button variant='secondary' size='sm' onClick={onBackToOverview} selectorClassName='settings-back-to-all'>
					<BackArrowIcon fillColor='currentColor' />
					Back to all settings
				</Button>

				{/* A dropdown rather than a row of segments: eight group names with their counts only fit
				    one row by scrolling, and the groups you are not in are worth space only while
				    switching. Collapsed, it leaves room for the back button on the same line. */}
				<Select value={group.category} onValueChange={category => onOpenGroup(category as SettingCategory)}>
					<SelectTrigger aria-label='Settings group' styles={styles.groupSelectTrigger}>
						<span>{optionLabel(group)}</span>
					</SelectTrigger>
					<SelectContent>
						{groups.map(candidate => (
							<SelectItem key={candidate.category} value={candidate.category}>
								{optionLabel(candidate)}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
			</div>

			<div className={css(styles.groupPanel)}>
				<div className={css(styles.groupPanelHeader)}>
					<Icon className={css(styles.cardIcon)} />
					<span className={css(styles.groupPanelTitle)}>{group.category}</span>
					{/* Same treatment as the hub cards, so the description reads the same way in both places. */}
					<HoverCard openDelay={100}>
						<HoverCardTrigger
							className={css(styles.cardInfoIcon)}
							aria-label={`${group.category}: ${group.description}`}
						>
							<InfoIcon fillColor='currentColor' />
						</HoverCardTrigger>
						<HoverCardContent styles={[styles.cardTooltip]}>{group.description}</HoverCardContent>
					</HoverCard>
				</div>
				<div className={css(styles.groupPanelBody, styles.groupPanelBodyColumns)}>
					{dependencyGroups.length === 0 ? (
						<div className={css(styles.emptyState)}>No settings in this group.</div>
					) : (
						dependencyGroups.map(({ parent, dependents }, index) => (
							// Wrapped because a chunk with no dependents renders as its bare row, which leaves
							// nothing for the column-break rule to attach to.
							<div key={getEntryKey(parent, index)} className={css(styles.groupPanelBodyItem)}>
								<SettingDependencyChunk
									parent={parent}
									dependents={dependents}
									hideDivider={index === dependencyGroups.length - 1}
									parentBadge={
										dependents.length > 0 ? (
											<span
												className={css(
													styles.dependentCountBadge,
													parent.valueText === 'On' && styles.dependentCountBadgeActive
												)}
											>
												{dependents.length} dependent {dependents.length === 1 ? 'setting' : 'settings'}
											</span>
										) : undefined
									}
								/>
							</div>
						))
					)}
				</div>
			</div>
		</>
	);
}
